"use client";

import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { LOCALES, isLocale, type LocaleCode } from "@/lib/i18n/locales";
import { LOCATIONS_LIST } from "@/extensions/glossary/glossary";
import Geoloc from "@/extensions/geoloc/geoloc";
import "./language.css";

/**
 * "Quick settings" — language + location/currency chooser (old __rby__cll).
 * Language swaps the /[locale] segment; location/currency are persisted
 * (they'll drive <Price> once a currency context lands). geoloc flags the
 * recommended location for the visitor.
 */
export function LanguageModal({
  locale,
  onClose,
}: {
  locale: LocaleCode;
  onClose: () => void;
}) {
  const router = useRouter();
  const pathname = usePathname();

  const [location, setLocation] = useState<string>("");
  const [recommended, setRecommended] = useState<string>("");

  useEffect(() => {
    try {
      setLocation(localStorage.getItem("rubyn.location") ?? "");
    } catch {
      /* ignore */
    }
    // Best-effort detection (graceful fallback) to flag the recommended row.
    Geoloc()
      .then((g) => setRecommended(g.location))
      .catch(() => {});
  }, []);

  function chooseLanguage(next: LocaleCode) {
    try {
      localStorage.setItem("rubyn.locale", next);
    } catch {
      /* ignore */
    }
    const segments = (pathname || `/${locale}`).split("/");
    if (isLocale(segments[1] ?? "")) segments[1] = next;
    else segments.splice(1, 0, next);
    router.push(segments.join("/") || `/${next}`);
    onClose();
  }

  function chooseLocation(name: string, currency: string) {
    try {
      localStorage.setItem("rubyn.location", name);
      localStorage.setItem("rubyn.currency", currency);
    } catch {
      /* ignore */
    }
    setLocation(name);
  }

  return (
    <div className="rubyn-cll" onClick={onClose}>
      <div className="rubyn-cll-container" onClick={(e) => e.stopPropagation()}>
        <div className="rubyn-cll-title">
          <h4>Quick settings</h4>
        </div>

        <div className="rubyn-cll-content">
          <div className="rubyn-cll-group">
            <h5>Select Language</h5>
            <div className="rubyn-cll-list">
              {LOCALES.map((l) => (
                <div
                  key={l.code}
                  className={`rubyn-cll-el ${l.code === locale ? "active" : ""}`}
                  onClick={() => chooseLanguage(l.code)}
                >
                  <h6>{l.label}</h6>
                  <span className="rubyn-cll-code">{l.code.toUpperCase()}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rubyn-cll-group">
            <h5>Select Location</h5>
            <div className="rubyn-cll-list">
              {LOCATIONS_LIST.map((loc) => {
                const active = loc.name === location;
                const isRec = loc.name === recommended;
                return (
                  <div
                    key={loc.code}
                    className={`rubyn-cll-el ${active ? "active" : ""}`}
                    onClick={() => chooseLocation(loc.name, loc.currency)}
                  >
                    <span className="rubyn-cll-flag">{loc.code}</span>
                    <h6>
                      {loc.name}
                      {isRec ? " · Recommended" : ""}
                    </h6>
                    <span className="rubyn-cll-code">
                      {loc.currency} {loc.currencySymbol}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="rubyn-cll-controller">
          <button className="rubyn-cll-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
